import Application from '../Application';

/**
 * The tool class is barely a class, it's more of a pattern. Subclasses should
 * override the abstract methods and add whatever additional methods they might
 * need. The pattern for a tool is that its begin method should be called by an
 * event handler. The begin method then sets up any event handlers that are
 * then required. The cancel and end methods should tear down those event
 * handlers. Subclasses might add additional methods such as drag (usually an
 * event listener listening for mouse movenments).
 *
 * Recall that using JavaScript methods as event handlers can cause the method
 * to loose its binding to the `this` keyword; subclasses may want to bind
 * their own methods in their constructor like Tool does with the abstract
 * methods. Subclasses don't have to re-bind those methods, they're done when
 * calling super() in the subclass's constructor.
 */
export default abstract class Tool {

    /**
     * This constructor is really the raison d'etre for the abstract class: it
     * binds all the abstract methods to this so that implementations get these
     * benefits from calling super();
     */
    constructor() {
        this.enable = this.enable.bind(this);
        this.disable = this.disable.bind(this);
        this.begin = this.begin.bind(this);
        this.cancel = this.cancel.bind(this);
        this.end = this.end.bind(this);
    }

    /**
     * Enabling the tool allows interacitons to be started. Side effects of
     * enabling may include attaching the begin method to an event handler.
     */
    abstract enable(): void;

    /**
     * Disabling the tool means that it should no longer interact with the
     * user. This is probably accomplished by the implementation removing any
     * relevant event listeners.
     */
    abstract disable(): void;

    /**
     * The begin method starts an interaction, e.g. by being called by an event
     * handler.
     */
    abstract begin(event?: Event): void;

    /**
     * Any action that ends without changing the state of the app should go
     * through 'cancel' to tear down the state of the tool. At the end of the
     * cancel method, the class should be ready for another call to activate.
     */
    abstract cancel(): void;

    /**
     * The end method is very similar to cancel in that it ends the tool's
     * lifecycle, but in addition to resetting the state of the tool, it also
     * asks the app to create a new entry in the command history.
     */
    abstract end(event?: Event): void;

}
